/**
 * Protected Simulator Component
 * Protects the mileage simulator with token-based access control
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock, AlertTriangle, Shield } from 'lucide-react';

interface ProtectedSimulatorProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'mileage_simulator_access';
const TOKEN = import.meta.env.VITE_MILEAGE_SIMULATOR_TOKEN;

export function ProtectedSimulator({ children }: ProtectedSimulatorProps) {
  const [searchParams] = useSearchParams();
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // SECURITY: If token is not configured, DENY access
    if (!TOKEN || TOKEN === '' || TOKEN === 'undefined') {
      console.error('VITE_MILEAGE_SIMULATOR_TOKEN not configured. Access DENIED.');
      setHasAccess(false);
      setIsChecking(false);
      return;
    }

    // Check URL parameter
    const urlToken = searchParams.get('access');

    // Check sessionStorage
    const storedToken = sessionStorage.getItem(STORAGE_KEY);

    if (urlToken && urlToken === TOKEN) {
      // Valid token in URL - grant access and store
      sessionStorage.setItem(STORAGE_KEY, TOKEN);
      setHasAccess(true);
    } else if (storedToken && storedToken === TOKEN) {
      // Valid token in session - grant access
      setHasAccess(true);
    } else {
      // No valid token found - DENY access
      setHasAccess(false);
    }

    setIsChecking(false);
  }, [searchParams]);

  // Loading state
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-[#B05D41] mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  // Access granted
  if (hasAccess) {
    return <>{children}</>;
  }

  // Access denied
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-500/5 via-transparent to-transparent" />

      <div className="relative flex items-center justify-center min-h-screen px-4">
        <div className="max-w-md w-full">
          {/* Lock Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500/20 mb-4">
              <Lock className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-100 mb-2">
              Acesso Restrito
            </h1>
            <p className="text-zinc-400">
              Esta ferramenta é de uso exclusivo da Travel Legacy
            </p>
          </div>

          {/* Warning Card */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-400 mb-1">
                  Simulador Protegido
                </h3>
                <p className="text-xs text-zinc-400">
                  O Simulador de Milhas requer um link de acesso especial.
                  Se você é cliente da Travel Legacy e precisa acessar esta ferramenta,
                  entre em contato com nossa equipe.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-zinc-800/50">
              <p className="text-xs text-zinc-500 mb-3">
                Para obter acesso:
              </p>
              <ul className="space-y-2 text-xs text-zinc-400">
                <li className="flex items-start gap-2">
                  <span className="text-[#B05D41] mt-0.5">•</span>
                  <span>Entre em contato com a Travel Legacy</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B05D41] mt-0.5">•</span>
                  <span>Solicite o link de acesso personalizado</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B05D41] mt-0.5">•</span>
                  <span>Use o link fornecido para acessar o simulador</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-zinc-500">
              Travel Legacy © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
